import React, { useRef, useContext } from 'react';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { getContext, useCombinedRefs } from 'common';
import Label, { LabelProps } from 'components/Label';
import Checkbox, { CheckboxProps } from 'components/Checkbox';
import { ListContext, ListItemText } from 'components/List';
import { CONSTANTS } from 'common/ListKeyboardNav';
import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM
} = CONSTANTS;

export interface MultiSelectSmartListCheckboxProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /** Text displayed as list item text. */
  label: string | React.ReactNode;
  /** Icon Component to place along with the label. */
  icon?: React.ReactNode;
  /** Text displayed as list item sub text, generally used as label description. */
  subLabel?: string;
  /** Display the list item as selected. */
  selected?: boolean;
  /** Display the list item as disabled. */
  disabled?: boolean;
  /** Override `Checkbox` props. */
  checkboxProps?: Partial<CheckboxProps>;
  /** Override `Label` props for checkbox label. */
  labelProps?: Partial<LabelProps>;
  /**
   * Callback fired when the value is changed. Signature: `function(event: object) => void`, event: The event source of the callback.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement> | React.MouseEventHandler;
  /**
   * @ignore
   * onKeyDown handler
   * */
  onKeyDown?: React.KeyboardEventHandler;
}

/** Smart list checkbox item.
 * Any other props than 'MultiSelectSmartListCheckbox' props will be provided to the root element.
 */
const MultiSelectSmartListCheckbox = React.forwardRef<HTMLElement, MultiSelectSmartListCheckboxProps>((props: MultiSelectSmartListCheckboxProps, forwardedRef) => getContext(({ RTL, theme }) => {
  const {
    label,
    subLabel,
    icon,
    selected,
    disabled,
    checkboxProps,
    labelProps,
    onChange,
    onKeyDown,
    ...otherProps
  } = props;
  const {
    inputProps,
    ...otherCheckboxProps
  } = checkboxProps;
  const resolvedTheme = theme.LIST.VERTICAL;
  const LIST_ITEM_SUB_LABEL_STYLE = {
    color: disabled ? resolvedTheme.disabled.text : resolvedTheme.secondaryText,
    ...Tokens.TYPOGRAPHY.CAPTION
  };
  const listContext = useContext(ListContext);
  const innerRef = useRef(null);
  const ref = useCombinedRefs(forwardedRef, innerRef);

  const handleCheckBoxToggle = (event) => {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    event.persist();
    onChange && onChange(event);
  };

  const handleListItemKeyDown = (event) => {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    event.persist();
    onKeyDown && onKeyDown(event);
  };
  return (
    <Styled.SmartListItem
      className={`${LIST_ITEM_TABABLE_ITEM}-${listContext.uniqueListId}`}
      ref={ref}
      RTL={RTL}
      $theme={resolvedTheme}
      hoverEffect
      disabled={disabled}
      tabIndex={-1}
      role="listitem"
      hasSecondaryLabel={subLabel}
      onFocus={event => event.preventDefault()}
      onClick={handleCheckBoxToggle}
      onKeyDown={handleListItemKeyDown}
      {...otherProps}
    >
      <Styled.SmartListItemCheckbox rtl={RTL} disabled={disabled} $theme={resolvedTheme}>
        <Checkbox
          inputProps={{
            tabIndex: -1,
            ...inputProps
          }}
          checked={selected}
          onChange={handleCheckBoxToggle}
          {...otherCheckboxProps}
        />
      </Styled.SmartListItemCheckbox>
      <Label {...labelProps}>
        <Styled.SmartListItemTextWrapper>
          <ListItemText disabled={disabled}>
            {label}
          </ListItemText>
          {subLabel && (
            <ListItemText rtl={RTL} $theme={resolvedTheme} disabled={disabled} style={LIST_ITEM_SUB_LABEL_STYLE}>
              {subLabel}
            </ListItemText>
          )}
        </Styled.SmartListItemTextWrapper>
      </Label>
      {icon && (
        <Styled.SmartListItemIconWrapper RTL={RTL} $theme={resolvedTheme} disabled={disabled}>
          {icon}
        </Styled.SmartListItemIconWrapper>
      )}
    </Styled.SmartListItem>
  );
}));

MultiSelectSmartListCheckbox.displayName = 'MultiSelectSmartListCheckbox';

MultiSelectSmartListCheckbox.defaultProps = {
  icon: undefined,
  subLabel: undefined,
  selected: false,
  disabled: false,
  checkboxProps: {},
  labelProps: {},
  onChange: undefined,
  onKeyDown: undefined,
};

export default MultiSelectSmartListCheckbox;
